import { Inject, Injectable } from '@angular/core';
import { fromEvent, Observable } from 'rxjs';
import { filter, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService, SEBValidatorItem } from './auth.service';
import { RoutesService } from './routes.service';
import { EDeliveryOption } from './models/db/test-sessions.schema';
import { DOCUMENT } from '@angular/common';

export const MPT_REMOTE_PROCTOR_DOMAIN = 'https://vea-mpt-remote-proctor-qc.vretta.com';
// export const MPT_REMOTE_PROCTOR_DOMAIN = 'https://sfu.mirotalk.com';

//LOCAL
// export const MPT_REMOTE_PROCTOR_DOMAIN = 'http://localhost:3010';

export const MIROTALK_EVENT_MESSAGE = 'mirotalk_event';

export const EXCHANGE_CODE_KEY = 'x-code';

export interface IMirotalkMeetingsResponse {
  roomId: string;
  peers: IMirotalkPeers[];
}

interface IMirotalkPeers {
  name: string,
  presenter: boolean,
  video: boolean,
  audio: boolean,
  screen: boolean,
  hand: boolean,
  os?: string,
  browser?: string
}

export interface IMirotalkEventMessage {
  type: string | typeof MIROTALK_EVENT_MESSAGE;
  data: any;
  event: string | MIROTALK_MESSAGE_EVENT_LIST;
  direction: 'outgoing' | 'incoming' | string;
  timestamp: number;
};

export interface MirotalkMessageEvent extends MessageEvent {
  data: IMirotalkEventMessage;
}

export enum MIROTALK_MESSAGE_EVENT_LIST {
  IS_ROOM_ACTIVE = 'isRoomActive',
  PEER_STATUS = 'peer_status',
  VIDEO_STATUS_CHANGED = 'videoStatusChanged',
  AUDIO_STATUS_CHANGED = 'audioStatusChanged',
}

@Injectable({
  providedIn: 'root'
})
export class RemoteProctoringService {


  public mirotalkEventMessage$: Observable<MirotalkMessageEvent> | null = null;



  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  public getMirotalkDomain() {
    return MPT_REMOTE_PROCTOR_DOMAIN;
  }

  generateRoomId(testSessionId: number, sessionInvitCode: string): string {
    // return `procto-${testSessionId}`;
    return `procto-${testSessionId}-${sessionInvitCode}`;
  }

  generatePrivateRoomId(testSessionId: number, uid: string) {
    return `private-${testSessionId}-${uid}`;
  }

  generateIdVerifRoomId(testSessionId: number, sessionInvitCode: string): string {
    // return `procto-${testSessionId}`;
    return `idverif-${testSessionId}-${sessionInvitCode}`;
  }
  // TODO: to keep explorign with invigilator DB records to check
  public async getMirotalkToken () {
    try {
      const res = await this.auth.apiGet(this.routes.REMOTE_PROCTORING_ACCESS_TOKEN, 0, {})
      return res
    } catch (error) {
      console.error("Error getting RP token data:", error);
    }
  }

  private getMessageEventObservable (): Observable<MirotalkMessageEvent> {
    if (this.mirotalkEventMessage$) return this.mirotalkEventMessage$;
    return this.mirotalkEventMessage$ = fromEvent(window, 'message')
      .pipe(
        filter((event: MessageEvent) => event.data && event.data.type === MIROTALK_EVENT_MESSAGE),
        shareReplay({ bufferSize: 1, refCount: true }) // provides new subscribers the latest event immediately
      );
  }
  public listenMirotalkEventMessage (eventName?: string): Observable<MirotalkMessageEvent> {
     return this.getMessageEventObservable().pipe(
      filter((event: MessageEvent) => eventName ? event.data.event === eventName : true),
     );
  }

  getRoomActiveEventMessage(): Observable<MirotalkMessageEvent> {
    return this.listenMirotalkEventMessage(MIROTALK_MESSAGE_EVENT_LIST.IS_ROOM_ACTIVE);
  }

  async getMirotalkActiveRooms () : Promise<IMirotalkMeetingsResponse[]> {
    try {
      const res = await this.auth.apiGet(this.routes.REMOTE_PROCTORING_ACTIVE_MEETINGS, 0, {})
      return res as IMirotalkMeetingsResponse[];
    } catch (error) {
      console.error("Error fetching mirotalk api meetings data:", error);
    }
  }
  async isRoomReady (roomId: string) {
    const meetings = await this.getMirotalkActiveRooms();
    if (!meetings.length) {
      return false;
    }
    const room = meetings.find(room => {
    return room.roomId === roomId;
});
    if (!room) {
      return false;
    }
    if (!this.isTherePeerPresenter(room.peers)) {
      return false;
    }
    return true;
  }
  isTherePeerPresenter (roomPeers: IMirotalkPeers[]) {
    if (!roomPeers.length) {
      return false;
    }
    const peerPresenter = roomPeers.find(peer => peer.presenter);
    console.log("🚀 ~ remote-proctoring.service.ts:161 ~ RemoteProctoringService ~ isTherePeerPresenter ~ peerPresenter:", peerPresenter)
    if (!peerPresenter) {
      return false;
    }
    console.log('FOUND PRESENTER - WILL JOIN')
    return true;
  }

  async getPresenterCallLink (roomId: string) {

  }

  async getPublicCallLink (roomId: string) {

  }

  async checkIfRedirectToEmbeddedProctoring(path: string, deliveryFormat?: EDeliveryOption | string) {
    // Redirect to main when no bookings
    if (!deliveryFormat && environment.isAltDomain) {
      await this.redirectSecurelyToOtherDomain(environment.remoteRedirectUrl, path);
      return true;
    }
    
    if (deliveryFormat === EDeliveryOption.REMOTE_EMBEDDED && !environment.isAltDomain) {
      await this.redirectSecurelyToOtherDomain(environment.remoteRedirectUrl, path);
      return true;
    }
    if (deliveryFormat !== EDeliveryOption.REMOTE_EMBEDDED && environment.isAltDomain) {
      await this.redirectSecurelyToOtherDomain(environment.remoteRedirectUrl, path);
      return true;
    }
    return false;
  }

  formatUsername (username: string) {
    if (username.split(' ').length > 1) {
      return username.split(' ').join('_');
    }
    return username;
  }

  async getSessionAdminLink (sessionId: number, sessionCode: string, userName: string, lang: string) {
    const token = await this.getMirotalkToken();
    const roomId = this.generateRoomId(sessionId, sessionCode);
    const urlStringSfu = `${MPT_REMOTE_PROCTOR_DOMAIN}/join?room=${roomId}&name=${userName}&avatar=0&audio=1&video=0&screen=0&hide=0&notify=0&token=${token}&lang=${lang}`;
    return urlStringSfu;
  }

  async getPrivateAdminLink (sessionId: number, candUid: number, userName: string, lang: string) {
    const token = await this.getMirotalkToken();
    const roomId = this.generatePrivateRoomId(sessionId, ''+candUid);
    const urlStringSfu = `${MPT_REMOTE_PROCTOR_DOMAIN}/join?room=${roomId}&name=${userName}&avatar=0&audio=1&video=0&screen=0&hide=0&notify=0&token=${token}&lang=${lang}`;
    return urlStringSfu;
  }

  getSessionCandidateLink (sessionId: number, sessionCode: string, userName: string, lang: string) {
    const roomId = this.generateRoomId(sessionId, sessionCode);
    const urlStringSfu = `${MPT_REMOTE_PROCTOR_DOMAIN}/join?room=${roomId}&name=${userName}&avatar=0&audio=1&video=0&screen=0&hide=0&notify=0&lang=${lang}`;
    return urlStringSfu;
  }

  getPrivateCandidateLink (sessionId: number, candUid: number, userName: string, lang: string) {
    const roomId = this.generatePrivateRoomId(sessionId, ''+candUid);
    const urlStringSfu = `${MPT_REMOTE_PROCTOR_DOMAIN}/join?room=${roomId}&name=${userName}&avatar=0&audio=1&video=0&screen=0&hide=0&notify=0&lang=${lang}`;
    return urlStringSfu;
  }
  
  async redirectSecurelyToOtherDomain(targetBaseUrl: string, path: string) {
    const sebStatus = this.auth.getSEBValidatorStatus();

    // get access code
    const res = await this.auth.apiCreate(this.routes.AUTH_EXCH_CODE, {action: 'generate'});

    // construct destination - add code
    let destination = `${targetBaseUrl}/#${path}`; // use /# because using HashLocationStrategy

    const params = new URLSearchParams();
    if (res.code) params.append(EXCHANGE_CODE_KEY, res.code);

    //attach SEB Status
    if (sebStatus) {
        params.append(SEBValidatorItem, sebStatus.toString());
    }

    // redirect
    this.document.location.href = `${destination}?${params.toString()}`;
    return;
  }
}
